/**
 * Ключ идемпотентности одной ПОПЫТКИ, а не одного нажатия.
 *
 * Разница не теоретическая, и она уже стоила денег. Ключ, созданный прямо в
 * обработчике нажатия, защищает ровно от двойного щелчка мышью — и ни от чего
 * больше. Опасный случай другой: сервер запрос принял, а ответ до нас не
 * доехал (обрыв, спящий ноутбук, `403 step_up_required` с последующим
 * повтором). Повтор с новым ключом уходит как ВТОРОЙ запрос, и сервер честно
 * выполняет его второй раз: второе списание, второе письмо, второе клиентское
 * пространство.
 *
 * Отсюда правило: ключ рождается вместе с намерением — при открытии окна, — и
 * живёт через все повторы до исхода.
 *
 * **Исход бывает известным и неизвестным, и только это решает судьбу ключа.**
 * `renew()` в закрытии окна выглядит безобидно ровно до первого обрыва связи:
 * человек нажал «Отмена» (или Escape) на висящем запросе, ключ сменился, а
 * сервер тем временем закоммитил — следующая отправка уходит НОВЫМ ключом и
 * заводит второго клиента. Поэтому ключ переживает закрытие окна: пока никто
 * не ответил, попытка не кончилась, чего бы ни нажимал человек.
 *
 * Что считается известным исходом (`answered`): сервер ответил — успехом или
 * отказом. На не-2xx сервер ключ отпускает (`platform/idempotency.py`,
 * `_release`), так что следующая попытка вправе взять новый; на успехе ответ
 * записан, и повтор того же ключа с другим телом получит
 * `409 idempotency_key_reused` — это тоже ответ, и он тоже исход. Не исход
 * ровно одно: `idempotency_in_flight` («запрос с этим ключом ещё выполняется»)
 * и любое молчание сети.
 *
 * Механика тут одна на всех и потому лежит одна: `useRebuildAttempt` в
 * `ui/preparation.tsx` — это она же под своим именем.
 */

import { useState } from 'react';

import { idempotencyKey } from '../api/client';
import { ApiError } from '../api/problem';

export interface Attempt {
  /** Ключ, с которым уходят все повторы этой попытки. */
  readonly key: string;
  /**
   * Попытка кончилась: следующая пойдёт с новым ключом.
   *
   * Зовётся там, где кончилось НАМЕРЕНИЕ, — на закрытии окна и на успехе. Если
   * исход последней отправки неизвестен, просьба не выполняется: повтор обязан
   * уйти с тем же ключом, иначе сервер увидит второй запрос.
   */
  renew: () => void;
  /**
   * Отправка под ключом попытки. Ею и определяется, стал ли исход известен.
   *
   * Отправлять мимо `send` можно (так делает пересборка ядра, где `renew()`
   * зовётся только после ответа), но тогда неизвестный исход ключ не удержит.
   */
  send: <T>(run: (key: string) => Promise<T>) => Promise<T>;
}

/**
 * Ответил ли сервер — то есть знаем ли мы, что стало с попыткой.
 *
 * `idempotency_in_flight` — ответ, но не исход: сервер говорит «этот же ключ
 * прямо сейчас выполняется». Новый ключ после него — это ровно второй запрос
 * поверх ещё не законченного первого.
 */
function answered(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return error.problem.code !== 'idempotency_in_flight';
}

/**
 * Попытка без React — её и проверяет приёмка (`tools/check-agency-create.mjs`).
 *
 * Состояние здесь не реактивное намеренно: ключ никто не рисует, его читают в
 * обработчике отправки. Перерисовка на смене ключа означала бы, что окно
 * моргает на закрытии.
 */
export function openAttempt(mint: () => string = idempotencyKey): Attempt {
  let key = mint();
  /** Последняя отправка ещё не разрешилась или разрешилась молчанием сети. */
  let unknown = false;

  return {
    get key() {
      return key;
    },
    renew() {
      if (unknown) return;
      key = mint();
    },
    async send<T>(run: (key: string) => Promise<T>): Promise<T> {
      unknown = true;
      try {
        const answer = await run(key);
        unknown = false;
        return answer;
      } catch (error) {
        if (answered(error)) unknown = false;
        throw error;
      }
    },
  };
}

export function useAttempt(): Attempt {
  // Состояние с ленивой начальной величиной, а не `useRef`: попытка читается
  // на отрисовке (форма берёт из неё ключ), а ref для читаемого на отрисовке
  // значения — ровно то, что запрещает `react-hooks/refs`. Обновление
  // состояния при этом не зовётся ни разу: объект один на всю жизнь окна, а
  // ключ меняется внутри него.
  const [attempt] = useState(openAttempt);
  return attempt;
}
